/**
 * What the header search finds on the account-level screens (Clients,
 * Projects, Invoices, Client Quotes, Sales Pipeline, Vendor Directory, Users).
 * Kept apart from SearchContext, which indexes a single project's records.
 *
 * Money rules: a match never reads an amount (genericMatch skips money-named
 * fields, and the record handed to it carries none), and the description line
 * only shows an invoice or quote total to someone who may see money. A person
 * who may not still finds the document by number, client and status.
 */
import { genericMatch } from './searchMatch';
import { formatMoney } from './currency';
import { entityPath } from './entityRoutes';
import { clientPath, clientInvoicePath, clientQuotePath } from './accountRoutes';
import { formatDate } from './formatDate';
import type { Client } from '../services/ClientService';
import type { ClientInvoice } from '../services/ClientInvoiceService';
import type { ClientQuote } from '../services/ClientQuoteService';
import type { AccountVendor } from '../services/AccountVendorService';
import type { Workspace } from './workspaces';
import type { Person } from '../types/People';

/**
 * Distinct from the per-project result types ('quote' is a vendor quote and
 * 'vendor' opens in the active project), so a result is never routed as the other.
 */
export type AccountSearchType = 'client' | 'project' | 'clientInvoice' | 'clientQuote' | 'accountVendor' | 'person';

export interface AccountSearchResult {
  id: string;
  type: AccountSearchType;
  title: string;
  description: string;
  category: string;
  data: Record<string, unknown>;
}

export interface AccountSearchData {
  clients: Client[];
  invoices: ClientInvoice[];
  quotes: ClientQuote[];
  /** The workspace's projects; each is also a card on the Sales Pipeline, at its stage. */
  projects: Workspace[];
  vendors: AccountVendor[];
  people: Person[];
}

export const EMPTY_ACCOUNT_SEARCH_DATA: AccountSearchData = {
  clients: [], invoices: [], quotes: [], projects: [], vendors: [], people: [],
};

/** Per-type cap, so one long list cannot crowd out the others. */
const PER_TYPE_LIMIT = 6;
const MAX_RESULTS = 30;

const STAGE_LABELS: Record<string, string> = {
  lead: 'Lead', proposal: 'Proposal', booked: 'Booked', delivering: 'Delivering', won: 'Won', lost: 'Lost',
};
const stageLabel = (stage: string): string => STAGE_LABELS[stage] ?? stage;
const label = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);
const joined = (...parts: Array<string | null | undefined | false>): string => parts.filter(Boolean).join(' · ');

/**
 * Whether a project (a Projects card, a Sales Pipeline card) matches a typed
 * term: its name, stage, your role in it and its date. Never its contract
 * value, which a financially blind role cannot see.
 */
export function matchesWorkspace(p: Workspace, term: string): boolean {
  const q = term.trim().toLowerCase();
  if (!q) return true;
  return genericMatch({ name: p.project_name, stage: stageLabel(p.pipeline_stage), role: p.role, date: p.project_date }, q);
}

/** Whether a person matches a typed term: name, e-mail, phones, notes and the projects they are on. */
export function matchesPerson(p: Person, term: string): boolean {
  const q = term.trim().toLowerCase();
  if (!q) return true;
  return genericMatch({
    name: p.name, email: p.email, phone: p.phone, whatsapp: p.whatsapp, notes: p.notes,
    projects: p.projects.map(x => x.project_name),
  }, q);
}

export interface AccountSearchOptions {
  canViewFinancials: boolean;
}

/** Results for a typed term, grouped client, project, invoice, quote, vendor, person. */
export function searchAccountRecords(query: string, data: AccountSearchData, opts: AccountSearchOptions): AccountSearchResult[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];

  const out: AccountSearchResult[] = [];
  const counts: Record<string, number> = {};
  const push = (r: AccountSearchResult) => {
    if ((counts[r.type] ?? 0) >= PER_TYPE_LIMIT) return;
    counts[r.type] = (counts[r.type] ?? 0) + 1;
    out.push(r);
  };
  const hit = (fields: Record<string, unknown>) => genericMatch(fields, q);

  data.clients.forEach(c => {
    if (!hit({ name: c.name, email: c.email, phone: c.phone, notes: c.notes })) return;
    push({
      id: `client-${c.id}`, type: 'client', title: c.name,
      description: joined(c.email, c.phone, `${c.project_count} ${c.project_count === 1 ? 'project' : 'projects'}`),
      category: 'Client', data: { id: c.id },
    });
  });

  data.projects.forEach(p => {
    if (!matchesWorkspace(p, q)) return;
    const date = p.project_date ? formatDate(new Date(p.project_date)) : '';
    push({
      id: `project-${p.project_id}`, type: 'project', title: p.project_name,
      description: joined(stageLabel(p.pipeline_stage), date),
      category: 'Project', data: { id: p.project_id },
    });
  });

  data.invoices.forEach(inv => {
    if (!hit({ number: inv.invoice_number, client: inv.client_name, status: inv.status, notes: inv.notes })) return;
    push({
      id: `invoice-${inv.id}`, type: 'clientInvoice', title: inv.invoice_number,
      description: joined(inv.client_name, label(inv.status), opts.canViewFinancials && formatMoney(inv.total, inv.currency)),
      category: 'Invoice', data: { id: inv.id },
    });
  });

  data.quotes.forEach(qt => {
    if (!hit({ number: qt.quote_number, client: qt.client_name, status: qt.status, notes: qt.notes })) return;
    push({
      id: `quote-${qt.id}`, type: 'clientQuote', title: qt.quote_number,
      description: joined(qt.client_name, label(qt.status), opts.canViewFinancials && formatMoney(qt.total, qt.currency)),
      category: 'Client quote', data: { id: qt.id },
    });
  });

  data.vendors.forEach(v => {
    if (!hit({ name: v.name, category: v.category, categories: v.categories, status: v.status, project: v.projectName, instagram: v.instagram })) return;
    push({
      id: `vendor-${v.id}`, type: 'accountVendor', title: v.name,
      description: joined(v.category, v.projectName),
      category: 'Vendor', data: { id: v.id, projectId: v.projectId },
    });
  });

  data.people.forEach(p => {
    if (!matchesPerson(p, q)) return;
    push({
      id: `person-${p.id}`, type: 'person', title: p.name,
      description: joined(p.email, p.login ? label(p.login.role) : 'No login'),
      category: 'Person', data: { id: p.id },
    });
  });

  return out.slice(0, MAX_RESULTS);
}

/**
 * Where a result opens. A project has no page of its own (opening it means
 * switching workspace), so it has no path; a vendor in another project also
 * needs the switch, which the caller does from `data.projectId`.
 */
export function accountResultPath(result: AccountSearchResult): string | null {
  const id = String(result.data.id ?? '');
  switch (result.type) {
    case 'client': return clientPath(id);
    case 'clientInvoice': return clientInvoicePath(id);
    case 'clientQuote': return clientQuotePath(id);
    // A vendor with no project has no page to open (the vendor route needs a project), so show the directory.
    case 'accountVendor': return result.data.projectId ? (entityPath('vendor', id) ?? '/vendor-directory') : '/vendor-directory';
    case 'person': return '/workspace/users';
    case 'project': return null;
  }
}
